import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Wallet, Plus, Pencil, Trash2, X, Archive, ArchiveRestore } from 'lucide-react';
import './Expenses.css';

const ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'loan', label: 'Loan' },
  { value: 'other', label: 'Other' }
];

const emptyAccount = {
  name: '',
  type: 'checking',
  institution: '',
  lastFour: '',
  notes: '',
  archived: false
};

export const getAccountTypeLabel = (v) => ACCOUNT_TYPES.find(t => t.value === v)?.label || v;

function Accounts() {
  const { state, addAccount, updateAccount, deleteAccount } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyAccount);
  const [showArchived, setShowArchived] = useState(false);

  const accounts = useMemo(() => {
    const list = (state.accounts || []).filter(a => showArchived ? true : !a.archived);
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [state.accounts, showArchived]);

  const txnCountByAccount = useMemo(() => {
    const map = {};
    (state.expenses || []).forEach(e => {
      if (e.accountId) map[e.accountId] = (map[e.accountId] || 0) + 1;
    });
    (state.deposits || []).forEach(d => {
      if (d.accountId) map[d.accountId] = (map[d.accountId] || 0) + 1;
    });
    (state.transfers || []).forEach(t => {
      if (t.fromAccountId) map[t.fromAccountId] = (map[t.fromAccountId] || 0) + 1;
      if (t.toAccountId) map[t.toAccountId] = (map[t.toAccountId] || 0) + 1;
    });
    return map;
  }, [state.expenses, state.deposits, state.transfers]);

  const openAdd = () => {
    setEditing(null);
    setFormData({ ...emptyAccount });
    setShowModal(true);
  };

  const openEdit = (acc) => {
    setEditing(acc);
    setFormData({ ...emptyAccount, ...acc });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    if (editing) {
      await updateAccount(editing.id, formData);
    } else {
      await addAccount(formData);
    }
    setShowModal(false);
  };

  const handleDelete = async (acc) => {
    const count = txnCountByAccount[acc.id] || 0;
    if (count > 0) {
      const ok = window.confirm(
        `"${acc.name}" is used by ${count} transaction${count === 1 ? '' : 's'}. ` +
        `Deleting will leave those transactions with a missing account. Consider archiving instead. Delete anyway?`
      );
      if (!ok) return;
    } else {
      if (!window.confirm(`Delete account "${acc.name}"?`)) return;
    }
    await deleteAccount(acc.id);
  };

  const toggleArchive = async (acc) => {
    await updateAccount(acc.id, { archived: !acc.archived });
  };

  return (
    <div className="expenses-page">
      <div className="expenses-header">
        <div className="header-title">
          <h1>Accounts</h1>
          <div className="subtitle">Bank, cash, and credit accounts used for expenses and income</div>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={openAdd}>
            <Plus size={16} /> Add Account
          </button>
        </div>
      </div>

      <div className="expenses-filters">
        <div className="filter-group">
          <label>View</label>
          <select value={showArchived ? 'all' : 'active'} onChange={e => setShowArchived(e.target.value === 'all')}>
            <option value="active">Active only</option>
            <option value="all">Include archived</option>
          </select>
        </div>
      </div>

      <div className="expenses-table-section">
        {accounts.length === 0 ? (
          <div className="expenses-empty">
            <Wallet size={48} />
            <p>No accounts yet — add your first one to start tagging transactions.</p>
          </div>
        ) : (
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Institution</th>
                <th>Last 4</th>
                <th className="right">Transactions</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acc => (
                <tr key={acc.id}>
                  <td>
                    <div>{acc.name}</div>
                    {acc.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{acc.notes}</div>}
                  </td>
                  <td><span className="account-badge">{getAccountTypeLabel(acc.type)}</span></td>
                  <td>{acc.institution || '—'}</td>
                  <td>{acc.lastFour ? `•••• ${acc.lastFour}` : '—'}</td>
                  <td className="right">{txnCountByAccount[acc.id] || 0}</td>
                  <td>
                    {acc.archived
                      ? <span className="status-badge overdue">Archived</span>
                      : <span className="status-badge paid">Active</span>}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button onClick={() => openEdit(acc)} title="Edit">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => toggleArchive(acc)} title={acc.archived ? 'Unarchive' : 'Archive'}>
                        {acc.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                      </button>
                      <button className="delete-btn" onClick={() => handleDelete(acc)} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit' : 'Add'} Account</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Chase Business Checking"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={e => setFormData({ ...formData, type: e.target.value })}
                  >
                    {ACCOUNT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Last 4 digits</label>
                  <input
                    type="text"
                    maxLength={4}
                    value={formData.lastFour}
                    onChange={e => setFormData({ ...formData, lastFour: e.target.value.replace(/\D/g, '') })}
                    placeholder="1234"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Institution</label>
                <input
                  type="text"
                  value={formData.institution}
                  onChange={e => setFormData({ ...formData, institution: e.target.value })}
                  placeholder="e.g. Chase, Wells Fargo"
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any notes about this account"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSave}>
                {editing ? 'Save Changes' : 'Add Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Accounts;
